import React from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { pdfSharedStyles } from '../../../pdfSharedStyles';
import { TFunction } from 'i18next';
import { IGeogridData, IGeogridItem } from '../../../../../api/IApiResponse';
import { ProductDataService } from '../../../../../services/ProductDataService';
import { getTensileAtStrain } from '../../../../../services/TensileAtStrength';
import { calculateGeogridRequiredStrengthUnified, calculateGeogridStrengthBreakdownUnified } from '../../../../../services/GeogridTtotService';
import { formatSubscriptString } from '../../../../../enum/SubscriptSymbol';

// Funzione di parsing base per <br> e <strong>
import { ValueUnitCells } from '../../shared/pdfUnitMerge';

function parseHtmlToPdfText(html: string, style?: any) {
    // Sostituisce <br> con \n
    const withBreaks = html.replace(/<br\s*\/??\s*>/gi, '\n');
    // Regex per trovare <strong>...</strong>
    const regex = /(<strong>(.*?)<\/strong>)/gi;
    const result: React.ReactElement[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(withBreaks)) !== null) {
        // Testo prima del tag
        if (match.index > lastIndex) {
            const text = withBreaks.substring(lastIndex, match.index);
            if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
        }
        // Testo in grassetto
        const boldText = match[2];
        if (boldText) result.push(
            <Text
                key={key++}
                style={{ fontFamily: 'DejaVuSans', fontWeight: 'bold', ...style }}
            >
                {boldText}
            </Text>
        );
        lastIndex = regex.lastIndex;
    }
    // Testo dopo l'ultimo tag
    if (lastIndex < withBreaks.length) {
        const text = withBreaks.substring(lastIndex);
        if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
    }
    return result;
}
interface ContainerYardGeoGridCoreProps {
    geoGridData?: IGeogridData;
    geogridImage?: string;
    projectData: any;
    t: TFunction;
    showInputData?: boolean;
    showGeoGridImage?: boolean;
    showCourseResults?: boolean;
    customTitle?: string;
    isCombinedPdf?: boolean;
}

export const ContainerYardGeoGridCore: React.FC<ContainerYardGeoGridCoreProps> = ({
    geoGridData,
    geogridImage,
    projectData,
    t,
    showInputData = true,
    showGeoGridImage = true,
    showCourseResults = true,
    customTitle,
    isCombinedPdf = false,
}) => {
    const unitSystem = projectData?.unit || 'Metric';
    const projectType = projectData?.type || 'geogrid';
    const projectInputs = projectData?.projectInfo?.data;
    const projectMethod = (projectData?.method as string) || (projectInputs?.method as string) || 'bcr';
    const isParkingDeck = projectType === 'parking_deck' || projectType === 'parkingdeck';
    const projectTypeLabel = isParkingDeck ? t('project_type_parking_deck') || 'Parking Deck' : t('project_type_container_yard') || 'Container Yard';
    const geogridInfoDescriptionKey = isParkingDeck
        ? projectMethod === 'static'
            ? 'parkingdeck_static_GeoGrid_project_info_desc_pdf'
            : 'parkingdeck_bcr_GeoGrid_project_info_desc_pdf'
        : projectMethod === 'static'
          ? 'containeryard_static_GeoGrid_project_info_desc_pdf'
          : 'containeryard_bcr_GeoGrid_project_info_desc_pdf';
    const geogridInfoDescription = t(geogridInfoDescriptionKey) || t('generic_GeoGrid_project_info_desc_pdf') || 'Project Information';

    // Helper function to get unit based on system
    const getUnit = (metricUnit: string, imperialUnit: string) => {
        return unitSystem === 'Imperial' ? imperialUnit : metricUnit;
    };

    // Helper to format numbers to 2 decimal places
    const formatValue = (value: unknown): string => {
        if (typeof value === 'number') {
            return value.toFixed(2);
        }
        return String(value);
    };

    // Helper to render a generic input group as a table
    const renderInputGroupTable = (groupKey: string, groupData: any, geogridType?: string) => {
        if (!groupData) return null;

        // Fields to exclude based on user requirements and subgrade input method
        const subgradeExclusions: string[] = ['designStrain', 'subgradeInputMethod']; // Always exclude designStrain and subgradeInputMethod

        // Conditionally exclude CBR or Ev2 based on subgradeInputMethod
        if (groupKey === 'subgrade' && groupData.subgradeInputMethod) {
            if (groupData.subgradeInputMethod === 'cbr') {
                subgradeExclusions.push('deformationModulus'); // Hide Ev2 when CBR is selected
            } else if (groupData.subgradeInputMethod === 'ev2' || groupData.subgradeInputMethod === 'modulus') {
                subgradeExclusions.push('cbrSubgrade'); // Hide CBR when Ev2/modulus is selected
            }
        }

        const fieldsToExclude: Record<string, string[]> = {
            subgrade: subgradeExclusions,
            baseCourse: [],
            subBaseCourse: [],
        };

        // Field mapping for symbols and units
        const getFieldInfo = (groupKey: string, fieldName: string): { symbol: string; unit: string } => {
            const fieldMap: Record<string, Record<string, { symbol: string; metricUnit: string; imperialUnit: string }>> = {
                load: {
                    deckHalfWidth: { symbol: 'B', metricUnit: 'm', imperialUnit: 'ft' },
                    deckHalfLength: { symbol: 'L', metricUnit: 'm', imperialUnit: 'ft' },
                    uniformLoad: { symbol: 'q', metricUnit: 'kPa', imperialUnit: 'psf' },
                    axleLoad: { symbol: 'P', metricUnit: 'kN', imperialUnit: 'lbf' },
                    designAxleLoad: { symbol: 'P', metricUnit: 'kN', imperialUnit: 'lbf' },
                    tireInflationPressure: { symbol: 'p', metricUnit: 'kPa', imperialUnit: 'psi' },
                    equivalentStandardAxleLoad: { symbol: 'ESAL', metricUnit: '', imperialUnit: '' },
                },
                truck: {
                    wheelRadius: { symbol: 'R', metricUnit: 'm', imperialUnit: 'ft' },
                    tirePressure: { symbol: 'p', metricUnit: 'kPa', imperialUnit: 'psi' },
                    thickness: { symbol: 't', metricUnit: 'm', imperialUnit: 'ft' },
                },
                surfaceLayer: {
                    thickness: { symbol: 't', metricUnit: 'm', imperialUnit: 'ft' },
                    unitWeight: { symbol: 'γ', metricUnit: 'kN/m³', imperialUnit: 'pcf' },
                    loadSpreadingAngle: { symbol: 'α', metricUnit: '°', imperialUnit: '°' },
                },
                baseCourse: {
                    unitWeight: { symbol: 'γ', metricUnit: 'kN/m³', imperialUnit: 'pcf' },
                    frictionAngle: { symbol: 'φ', metricUnit: '°', imperialUnit: '°' },
                    cohesion: { symbol: 'c', metricUnit: 'kPa', imperialUnit: 'psf' },
                    loadSpreadingAngle: { symbol: 'α', metricUnit: '°', imperialUnit: '°' },
                },
                geogridBaseCourse: {
                    thickness: { symbol: 't', metricUnit: 'm', imperialUnit: 'ft' },
                },
                subBaseCourse: {
                    unitWeight: { symbol: 'γ', metricUnit: 'kN/m³', imperialUnit: 'pcf' },
                    frictionAngle: { symbol: 'φ', metricUnit: '°', imperialUnit: '°' },
                    cohesion: { symbol: 'c', metricUnit: 'kPa', imperialUnit: 'psf' },
                    loadSpreadingAngle: { symbol: 'α', metricUnit: '°', imperialUnit: '°' },
                },
                geogridSubBaseCourse: {
                    thickness: { symbol: 't', metricUnit: 'm', imperialUnit: 'ft' },
                },
                subgrade: {
                    cbrSubgrade: { symbol: 'CBR', metricUnit: '%', imperialUnit: '%' },
                    deformationModulus: { symbol: 'Ev2', metricUnit: 'MPa', imperialUnit: 'psi' },
                    designStrain: { symbol: 'ε', metricUnit: '%', imperialUnit: '%' },
                    safetyFactor: { symbol: 'FS', metricUnit: '-', imperialUnit: '-' },
                },
            };

            const field = fieldMap[groupKey]?.[fieldName];
            if (field) {
                return {
                    symbol: field.symbol,
                    unit: getUnit(field.metricUnit, field.imperialUnit),
                };
            }
            return { symbol: '-', unit: '-' };
        };

        let entries = Object.entries(groupData).filter(([, value]) => value !== null && value !== undefined && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'));

        // Filter out excluded fields
        const excludedFields = fieldsToExclude[groupKey] || [];
        entries = entries.filter(([field]) => !excludedFields.includes(field));

        // Special filter for 'load' group: show only fields relevant to geogridType
        if (groupKey === 'load' && geogridType) {
            const rectangularFields = ['deckHalfWidth', 'deckHalfLength', 'uniformLoad'];
            const circularFields = ['axleLoad', 'designAxleLoad', 'tireInflationPressure', 'equivalentStandardAxleLoad'];
            if (geogridType === 'Rectangular') {
                entries = entries.filter(([field]) => rectangularFields.includes(field));
            } else if (geogridType === 'Circular') {
                entries = entries.filter(([field]) => circularFields.includes(field));
            }
        }

        if (entries.length === 0) return null;

        return (
            <View style={pdfSharedStyles.colHalf}>
                <Text style={pdfSharedStyles.tableTitle}>{t(groupKey)}</Text>
                <View style={pdfSharedStyles.spacer0} />
                <View style={pdfSharedStyles.table}>
                    <View style={pdfSharedStyles.tableRow}>
                        <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                            <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                        </View>
                        <View style={pdfSharedStyles.tableColHeaderSymbol}>
                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                        </View>
                        <View style={pdfSharedStyles.tableColHeaderValue}>
                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                        </View>
                        <View style={pdfSharedStyles.tableColHeaderUnit}>
                            <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                        </View>
                    </View>
                    {entries.map(([field, value], idx) => {
                        const fieldInfo = getFieldInfo(groupKey, field);
                        return (
                            <View key={idx} style={[pdfSharedStyles.tableRow, idx % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]}>
                                <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                    <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{t(`${groupKey}.${field}`, field)}</Text>
                                </View>
                                <View style={pdfSharedStyles.tableColSymbol}>
                                    <Text style={pdfSharedStyles.tableCell}>{fieldInfo.symbol}</Text>
                                </View>
                                <ValueUnitCells value={formatValue(value)} unit={fieldInfo.unit} />
                            </View>
                        );
                    })}
                </View>
            </View>
        );
    };

    // Row types for PDF table
    type TableRowData = string[] | { type: 'fullRow'; text: string } | { type: 'productRow'; description: string; value: string };

    // Format course data for PDF table
    const formatCourseDataForPdfTable = (
        courseTitleKey: string,
        thickness: number,
        geogrids: IGeogridItem[],
        t: TFunction,
        projectGeoGridData?: IGeogridData,
        getUnitFn?: (metricUnit: string, imperialUnit: string) => string
    ): TableRowData[] => {
        const tableData: TableRowData[] = [];
        const courseName = t(courseTitleKey);
        // Use provided getUnit function or default to metric
        const unitFn = getUnitFn || ((metric: string) => metric);

        tableData.push([t('graphic.table.course_thickness', { course: courseName }) || `${courseName} Thickness`, 't', thickness.toFixed(2), unitFn('m', 'ft')]);

        geogrids.forEach((geogrid, index) => {
            const geogridNum = index + 1;
            const productFamily = geogrid.type;
            const productName = geogrid.selectedGeogrid;
            const designStrain = geogrid.designStrain;

            const courseType: 'base' | 'subbase' = courseTitleKey.includes('subbase') ? 'subbase' : 'base';
            const calculatedRequiredStrength = calculateGeogridRequiredStrengthUnified(projectGeoGridData, index, courseType);
            const calculatedNominalStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);
            const fallbackRequiredStrength = getTensileAtStrain(ProductDataService.getAllProducts(), productFamily, productName, designStrain);

            // Get tensile breakdown (Tz, Tp, Tm)
            const breakdown = calculateGeogridStrengthBreakdownUnified(projectGeoGridData, index, courseType);

            const numericReqStrength = calculatedRequiredStrength ?? fallbackRequiredStrength;
            const numericNomStrength = calculatedNominalStrength;

            const displayRequiredStrength = numericReqStrength?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A';
            const displayNominalStrength = numericNomStrength?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A';

            // Product row with colSpan - value spans symbol, value and unit columns
            tableData.push({ type: 'productRow', description: t('graphic.table.geogrid_product', { num: geogridNum }), value: geogrid.selectedGeogrid || t('common.not_available', 'N/A') });
            tableData.push([t('graphic.table.geogrid_position', { num: geogridNum }), formatSubscriptString(`p_${geogridNum}`), geogrid.position?.toFixed(2) || t('common.not_available', 'N/A'), unitFn('m', 'ft')]);
            tableData.push([t('graphic.table.geogrid_strain', { num: geogridNum }), formatSubscriptString(`ε_d${geogridNum}`), (geogrid.designStrain?.toString() || t('common.not_available', 'N/A')) ?? 'N/A', '%']);
            // Tensile contribution breakdown rows
            tableData.push([t('graphic.table.geogrid_earth_pressure'), formatSubscriptString(`T_z${geogridNum}`), breakdown?.Tz?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A', unitFn('kN/m', 'lbf/ft')]);
            tableData.push([t('graphic.table.geogrid_tension_component'), formatSubscriptString(`T_p${geogridNum}`), breakdown?.Tp?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A', unitFn('kN/m', 'lbf/ft')]);
            tableData.push([t('graphic.table.geogrid_membrane_effect'), formatSubscriptString(`T_m${geogridNum}`), breakdown?.Tm?.toFixed(2) ?? t('common.not_available', 'N/A') ?? 'N/A', unitFn('kN/m', 'lbf/ft')]);
            tableData.push([t('graphic.table.geogrid_req_strength', { num: geogridNum }), formatSubscriptString(`T_req${geogridNum}`), displayRequiredStrength, unitFn('kN/m', 'lbf/ft')]);
            tableData.push([t('graphic.table.geogrid_nom_strength', { num: geogridNum }), formatSubscriptString(`T_nom${geogridNum}`), displayNominalStrength, unitFn('kN/m', 'lbf/ft')]);

            let statusText: string;
            if (numericNomStrength !== null && numericReqStrength !== null && numericNomStrength >= numericReqStrength) {
                statusText = t('graphic.table.verified', 'Verified');
            } else if (numericNomStrength === null || numericReqStrength === null) {
                statusText = t('graphic.table.data_missing_for_verification', 'Data Missing for Verification');
            } else {
                statusText = t('graphic.table.not_verified', 'Not Verified');
            }
            tableData.push({ type: 'fullRow', text: statusText });
        });

        return tableData;
    };

    // Extract GeoGrid course data
    const baseThickness = geoGridData?.geogridBaseCourse?.thickness ?? 0;
    const subbaseThickness = geoGridData?.geogridSubBaseCourse?.thickness ?? 0;
    const baseGeogrids = geoGridData?.geogridBaseCourse?.geogrids ?? [];
    const subbaseGeogrids = geoGridData?.geogridSubBaseCourse?.geogrids ?? [];
    const tipoGeoGrid = geoGridData?.type;
    const projectGeoGridData = projectData?.projectInfo?.data?.geoGrid;

    const formattedBaseCourseData = formatCourseDataForPdfTable('geogrid_base_course', baseThickness, baseGeogrids, t, projectGeoGridData, getUnit);
    const formattedSubbaseCourseData = formatCourseDataForPdfTable('geogrid_subbase_course', subbaseThickness, subbaseGeogrids, t, projectGeoGridData, getUnit);

    if (!geoGridData) {
        return (
            <View style={pdfSharedStyles.section}>
                <Text style={pdfSharedStyles.projectInfoSubtitle}>{t('geogrid_data_not_available', 'GeoGrid data not available')}</Text>
            </View>
        );
    }

    return (
        <>
            {/* GeoGrid Project Header */}
            <View style={pdfSharedStyles.spacer1} />
            <Text style={[pdfSharedStyles.projectInfoProjectType]}>GROUNDMAC - {projectTypeLabel}</Text>
            <View style={pdfSharedStyles.spacer0} />
            <Text style={pdfSharedStyles.projectInfoSubtitle}>{parseHtmlToPdfText(geogridInfoDescription)}</Text>

            {/* Input Data Summary Section */}
            {showInputData && (
                <View style={pdfSharedStyles.section}>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, { textAlign: 'left' }]}>{t('input_data_summary')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.innSection}>
                        {/* Load Table */}
                        {renderInputGroupTable('load', geoGridData?.load, tipoGeoGrid?.geogridType)}

                        {/* Truck Table */}
                        {renderInputGroupTable('truck', geoGridData?.truck)}

                        {/* Surface Layer Table - only if thickness > 0 */}
                        {geoGridData?.surfaceLayer &&
                            typeof geoGridData.surfaceLayer === 'object' &&
                            'thickness' in geoGridData.surfaceLayer &&
                            Number(geoGridData?.surfaceLayer?.thickness || 0) > 0 &&
                            renderInputGroupTable('surfaceLayer', geoGridData?.surfaceLayer)}

                        {/* Base Course Table - only if baseCourse has properties */}
                        {geoGridData?.baseCourse &&
                            typeof geoGridData.baseCourse === 'object' &&
                            Object.values(geoGridData.baseCourse).some(value => value !== null && value !== undefined) &&
                            renderInputGroupTable('baseCourse', geoGridData?.baseCourse)}

                        {/* Sub Base Course Table - only if subBaseCourse has properties */}
                        {geoGridData?.subBaseCourse &&
                            typeof geoGridData.subBaseCourse === 'object' &&
                            Object.values(geoGridData.subBaseCourse).some(value => value !== null && value !== undefined) &&
                            renderInputGroupTable('subBaseCourse', geoGridData?.subBaseCourse)}
                    </View>

                    <View break />
                    <View style={pdfSharedStyles.innSection}>{renderInputGroupTable('subgrade', geoGridData?.subgrade)}</View>
                </View>
            )}

            {/* GeoGrid Layer Visualization */}
            {showGeoGridImage && geogridImage && (
                <View style={{ alignItems: 'center', width: '100%' }} wrap={false}>
                    <Image style={[pdfSharedStyles.graphicImage, { width: '100%', maxWidth: '100%' }]} src={geogridImage} />
                </View>
            )}

            {/* Results Section - Title only shown if there are geogrid results, placed after canvas */}
            {showCourseResults && ((baseThickness > 0 && baseGeogrids.length > 0) || (subbaseThickness > 0 && subbaseGeogrids.length > 0)) && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, pdfSharedStyles.resultTitle]}>{t('results') || 'Results'}</Text>
                </>
            )}

            {/* Base Course Results */}
            {showCourseResults && baseThickness > 0 && baseGeogrids.length > 0 && (
                <View style={pdfSharedStyles.section}>
                    <View break />
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={pdfSharedStyles.tableTitle}>{t('geogrid_base_course', 'Base Course Details')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.table}>
                        <View style={pdfSharedStyles.tableRow} fixed>
                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                            </View>
                        </View>
                        {formattedBaseCourseData.map((rowItem, rowIndex) => {
                            if (Array.isArray(rowItem)) {
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{rowItem[0]}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColSymbol}>
                                            <Text style={pdfSharedStyles.tableCell}>{rowItem[1]}</Text>
                                        </View>
                                        <ValueUnitCells value={rowItem[2]} unit={rowItem[3]} />
                                    </View>
                                );
                            } else if (rowItem.type === 'productRow') {
                                // Product row with value spanning symbol, value and unit columns
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{rowItem.description}</Text>
                                        </View>
                                        <View style={[pdfSharedStyles.tableColValue, { width: '70%' }]}>
                                            <Text style={pdfSharedStyles.tableCell}>{rowItem.value}</Text>
                                        </View>
                                    </View>
                                );
                            } else {
                                // Full row for status
                                const isVerified = !rowItem.text.includes('Not Verified');
                                return (
                                    <View key={rowIndex} style={pdfSharedStyles.tableRow} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, isVerified ? pdfSharedStyles.verifiedCol : pdfSharedStyles.notVerifiedCol]}>
                                            <Text style={{ ...pdfSharedStyles.verifiedColTableCell, textAlign: 'left', paddingLeft: 10 }}>{rowItem.text}</Text>
                                        </View>
                                    </View>
                                );
                            }
                        })}
                    </View>
                </View>
            )}

            {/* Subbase Course Results */}
            {showCourseResults && subbaseThickness > 0 && subbaseGeogrids.length > 0 && (
                <View style={pdfSharedStyles.section}>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={pdfSharedStyles.tableTitle}>{t('geogrid_subbase_course', 'Subbase Course Details')}</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    <View style={pdfSharedStyles.table}>
                        <View style={pdfSharedStyles.tableRow} fixed>
                            <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                                <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderSymbol}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderValue}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                            </View>
                            <View style={pdfSharedStyles.tableColHeaderUnit}>
                                <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                            </View>
                        </View>
                        {formattedSubbaseCourseData.map((rowItem, rowIndex) => {
                            if (Array.isArray(rowItem)) {
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{rowItem[0]}</Text>
                                        </View>
                                        <View style={pdfSharedStyles.tableColSymbol}>
                                            <Text style={pdfSharedStyles.tableCell}>{rowItem[1]}</Text>
                                        </View>
                                        <ValueUnitCells value={rowItem[2]} unit={rowItem[3]} />
                                    </View>
                                );
                            } else if (rowItem.type === 'productRow') {
                                // Product row with value spanning symbol, value and unit columns
                                return (
                                    <View key={rowIndex} style={[pdfSharedStyles.tableRow, rowIndex % 2 === 0 ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow]} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                                            <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{rowItem.description}</Text>
                                        </View>
                                        <View style={[pdfSharedStyles.tableColValue, { width: '70%' }]}>
                                            <Text style={pdfSharedStyles.tableCell}>{rowItem.value}</Text>
                                        </View>
                                    </View>
                                );
                            } else {
                                // Full row for status
                                const isVerified = !rowItem.text.includes('Not Verified');
                                return (
                                    <View key={rowIndex} style={pdfSharedStyles.tableRow} wrap={false}>
                                        <View style={[pdfSharedStyles.tableCol, isVerified ? pdfSharedStyles.verifiedCol : pdfSharedStyles.notVerifiedCol]}>
                                            <Text style={{ ...pdfSharedStyles.verifiedColTableCell, textAlign: 'left', paddingLeft: 10 }}>{rowItem.text}</Text>
                                        </View>
                                    </View>
                                );
                            }
                        })}
                    </View>
                </View>
            )}
        </>
    );
};
